/**
 * Seed Data Catalog — org-scoped view of seed data items.
 *
 * @module SeedDataCatalog
 * @see docs/api/jobs/suite-orchestrator.md
 */
import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Card, Flex, Text, Button, DataTable, PageHeader, Switch, TagBadge } from '@chatsdet/ui';
import {
  Plant,
  Plus,
  CaretDown,
  CaretRight,
  ArrowSquareOut,
  Lightning,
  FloppyDisk,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useSeedDataQuery, type SeedDataItem } from '../../hooks/useSeedData';
import { CreateSeedScriptDialog } from '../components/CreateSeedScriptDialog';
import { useWorkflowsQuery, type WorkflowListItemWithDetails } from '../../hooks/useWorkflows';
import {
  useSeedWorkflowOutputs,
  useUpdateSeedOutputs,
  type SeedDataOutput,
} from '../../hooks/useSeedWorkflowOutputs';
import { SeedOutputsEditor } from '../components/SeedOutputsEditor';
import { useUpdateTeardownWorkflow } from '../../hooks/useUpdateTeardownWorkflow';
import { useAddTeardown } from '../../hooks/useAddTeardown';

const columns: ColumnDef<StockFeatures, SeedDataItem>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => <Text weight="medium">{row.original.name}</Text>,
  },
  {
    accessorKey: 'displayLabel',
    header: 'Label',
    cell: ({ row }) => <Text size="sm">{row.original.displayLabel}</Text>,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground truncate max-w-[300px]">
        {row.original.description ?? '—'}
      </Text>
    ),
  },
  {
    accessorKey: 'dataKey',
    header: 'Data Key',
    cell: ({ row }) => (
      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{row.original.dataKey}</code>
    ),
  },
  {
    accessorKey: 'attributesJson',
    header: 'Attributes',
    cell: ({ row }) => {
      const attrs = row.original.attributesJson;
      if (!attrs || Object.keys(attrs).length === 0)
        return (
          <Text size="sm" className="text-muted-foreground">
            —
          </Text>
        );
      return (
        <Flex gap="xs" wrap="wrap">
          {Object.entries(attrs).map(([key, value]) => (
            <TagBadge
              name={
                <>
                  {key}: {String(value)}
                </>
              }
              color="gray"
              hideDot
              key={key}
            />
          ))}
        </Flex>
      );
    },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Last Updated',
    cell: ({ row }) => {
      const date = new Date(row.original.updatedAt);
      return (
        <Text size="sm" className="text-muted-foreground">
          {date.toLocaleDateString()}
        </Text>
      );
    },
  },
];

/** Inline output editor for a single seed workflow */
function SeedWorkflowOutputsSection({
  workflow,
  seedDataItems,
}: {
  workflow: WorkflowListItemWithDetails;
  seedDataItems: SeedDataItem[];
}) {
  const [expanded, setExpanded] = React.useState(false);
  const summaryOutputs = workflow.seedOutputsJson ?? [];
  const { data: outputsData } = useSeedWorkflowOutputs(workflow.id, {
    enabled: expanded,
    initialData: { outputs: summaryOutputs, workflowName: workflow.name },
  });
  const updateMutation = useUpdateSeedOutputs(workflow.id);
  const updateTeardownMutation = useUpdateTeardownWorkflow(workflow.id);
  const addTeardownMutation = useAddTeardown(workflow.id, workflow.name);
  const outputs = outputsData?.outputs ?? summaryOutputs;
  const hasTeardown = !!workflow.teardownWorkflowId;
  const teardownEnabled = workflow.teardownEnabled ?? false;

  // Local editing state — null means "no local edits, show server data"
  const [localOutputs, setLocalOutputs] = React.useState<SeedDataOutput[] | null>(null);
  const isDirty = localOutputs !== null;

  // Check which declared outputs have matching seed data
  const producedKeys = React.useMemo(
    () =>
      new Set(seedDataItems.filter((d) => d.seedWorkflowId === workflow.id).map((d) => d.dataKey)),
    [seedDataItems, workflow.id]
  );

  const handleChange = (newOutputs: SeedDataOutput[]) => {
    setLocalOutputs(newOutputs);
  };

  const handleSave = () => {
    if (!localOutputs) return;
    updateMutation.mutate(localOutputs, {
      onSuccess: () => setLocalOutputs(null),
    });
  };

  const handleTeardownToggle = (isSelected: boolean) => {
    updateTeardownMutation.mutate({ teardownEnabled: isSelected });
  };

  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        className="flex w-full items-center gap-2 p-3 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <CaretDown size={14} className="shrink-0" />
        ) : (
          <CaretRight size={14} className="shrink-0" />
        )}
        <div className="flex flex-col gap-0.5 min-w-0">
          <Text weight="medium" size="sm">
            {workflow.name}
          </Text>
          {workflow.description && (
            <Text size="xs" className="text-muted-foreground truncate">
              {workflow.description}
            </Text>
          )}
        </div>
        {outputs.some((o) => o.credentialOutput) && (
          <TagBadge
            name="Credential"
            color="yellow"
            icon={<Lightning size={10} weight="fill" />}
            className="ml-auto shrink-0"
          />
        )}
        <TagBadge
          name={
            <>
              {outputs.length} output{outputs.length !== 1 ? 's' : ''}
            </>
          }
          color="gray"
          hideDot
          className={outputs.some((o) => o.credentialOutput) ? 'shrink-0' : 'ml-auto shrink-0'}
        />
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        {hasTeardown ? (
          <div className="shrink-0 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Switch
              size="sm"
              isSelected={teardownEnabled}
              onChange={handleTeardownToggle}
              isDisabled={updateTeardownMutation.isPending}
              label="Teardown"
            />
          </div>
        ) : (
          <button
            className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              addTeardownMutation.mutate();
            }}
            disabled={addTeardownMutation.isPending}
          >
            <Plus size={10} />
            {addTeardownMutation.isPending ? 'Adding...' : 'Add Teardown'}
          </button>
        )}
        <Link
          to="/flows/$workflowId"
          params={{ workflowId: workflow.id }}
          className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          View Flow
          <ArrowSquareOut size={12} />
        </Link>
        {workflow.teardownWorkflowId && (
          <Link
            to="/flows/$workflowId"
            params={{ workflowId: workflow.teardownWorkflowId }}
            className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            View Teardown
            <ArrowSquareOut size={12} />
          </Link>
        )}
      </button>
      {expanded && (
        <div className="border-t border-border p-3">
          <Flex justify="end" className="mb-3">
            <Button
              variant="primary"
              size="sm"
              onPress={handleSave}
              isDisabled={!isDirty || updateMutation.isPending}
              iconLeft={<FloppyDisk size={14} />}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </Flex>
          <SeedOutputsEditor
            outputs={localOutputs ?? outputs}
            onChange={handleChange}
            producedKeys={producedKeys}
          />
        </div>
      )}
    </div>
  );
}

export function SeedDataCatalog() {
  const [page, setPage] = React.useState(1);
  const pageSize = 25;
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const { data, isLoading } = useSeedDataQuery({ page, pageSize });

  // Fetch seed workflows for the output editing section
  const { data: seedWorkflowsData } = useWorkflowsQuery({
    flowType: ['seed'],
    pageSize: 100,
  });
  const seedWorkflows = seedWorkflowsData?.items ?? [];

  const items = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <Flex direction="column" gap="lg">
      <PageHeader
        size="section"
        icon={<Plant size={18} className="text-muted-foreground" />}
        title="Seed Data"
        description="Seed scripts provision shared test data for your workflows."
        actions={
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Plus size={16} weight="bold" />}
            onPress={() => setIsDialogOpen(true)}
          >
            New Seed Script
          </Button>
        }
      />

      {/* Seed Workflow Outputs Section */}
      {seedWorkflows.length > 0 && (
        <Flex direction="column" gap="sm">
          {seedWorkflows.map((wf) => (
            <SeedWorkflowOutputsSection key={wf.id} workflow={wf} seedDataItems={items} />
          ))}
        </Flex>
      )}

      {items.length === 0 && !isLoading ? (
        seedWorkflows.length > 0 ? (
          <Text size="sm" className="text-muted-foreground text-center py-4">
            No seed data produced yet. Run a seed script to populate data.
          </Text>
        ) : (
          <Card className="p-8">
            <Flex direction="column" align="center" gap="md">
              <Plant size={48} className="text-muted-foreground" />
              <Text weight="medium">No seed data yet</Text>
              <Text size="sm" className="text-muted-foreground text-center max-w-md">
                Seed data is provisioned when seed scripts run as part of test suites. Create a seed
                script to get started.
              </Text>
              <Button
                variant="primary"
                iconLeft={<Plus size={18} weight="bold" />}
                onPress={() => setIsDialogOpen(true)}
              >
                New Seed Script
              </Button>
            </Flex>
          </Card>
        )
      ) : (
        <DataTable
          columns={columns}
          data={items}
          serverPagination={
            pagination
              ? {
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  totalItems: pagination.totalItems,
                  pageCount: pagination.totalPages,
                  onPageChange: setPage,
                  isLoading,
                }
              : undefined
          }
        />
      )}

      <CreateSeedScriptDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </Flex>
  );
}
